/**
 * Table Object
 * @author David <david.prenninger@gmx.de>
 *
 * This file provides all helper functions related to counter visualization.
 * This includes calculating verticies, faces and normales of objects.
 * Applying textures to it and calculating rounded corners.
 */
import * as THREE from 'three';
import { ASSET_FOLDER } from '@/constants';
import { degreeToMatrix, calculateIntersection } from '@/helpers/visualization/geometry';
import optimerFont from 'three/examples/fonts/optimer_bold.typeface.json';

const calculateVerticesAndFaces = (sides) => {
  const vertices = [];
  const faces = [];

  // FRONT
  vertices.push(
    new THREE.Vector3(sides[0].x, 0, 0),
    new THREE.Vector3(sides[0].x, sides[0].z, 0),
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, sides[0].z, 0),
  );
  faces.push(
    new THREE.Face3(0, 3, 1),
    new THREE.Face3(0, 2, 3),
  );

  let faceCount = 3;
  let faceP1 = 0;
  let faceP2 = 1;
  let faceP3 = 2;
  let faceP4 = 3;

  const frontLineDirectionMatrix = degreeToMatrix(0);
  let frontLineOrigin = new THREE.Vector3(0, 0, 0);

  let backLineDirectionMatrix = degreeToMatrix(0);
  let backLineOrigin = new THREE.Vector3(sides[0].x, 0, 0);

  sides.forEach((side, index) => {
    const frontLineVector = new THREE.Vector3(0, 0, side.y);

    frontLineVector.applyMatrix4(frontLineDirectionMatrix);
    frontLineVector.add(frontLineOrigin);
    // frontLineVector == corner Point A3

    const roundEdgeDirectionMatrix = frontLineDirectionMatrix.clone();
    frontLineDirectionMatrix.multiply(degreeToMatrix(side.childAngle));
    // frontLineDirectionMatrix pointing into direction of B

    const sideDirectionMatrix = frontLineDirectionMatrix.clone().multiply(degreeToMatrix(90));
    // side direction matrix ortogonal on frontLineDirectionMatrix

    frontLineOrigin = frontLineVector;
    // new frontLineOrigin == A3

    let backlineIntersect = null;

    if (index < sides.length - 1 && side.childAngle !== 0) {
      const backPoint = new THREE.Vector3(0, 0, 1);
      backPoint
        .applyMatrix4(sideDirectionMatrix)
        .multiplyScalar(sides[index + 1].x)
        .add(frontLineOrigin);

      const frontSideVector = new THREE.Vector3(0, 0, 1);
      frontSideVector.applyMatrix4(frontLineDirectionMatrix);

      const backlineVector = new THREE.Vector3(0, 0, 1);
      backlineVector.applyMatrix4(backLineDirectionMatrix);

      backlineIntersect = calculateIntersection(
        backPoint,
        frontSideVector,
        backLineOrigin,
        backlineVector,
      );
    } else {
      const backPoint = new THREE.Vector3(0, 0, 1);
      backPoint
        .applyMatrix4(sideDirectionMatrix)
        .multiplyScalar(side.x)
        .add(frontLineOrigin);
      backlineIntersect = backPoint;
    }

    backLineOrigin = backlineIntersect;
    backLineDirectionMatrix = frontLineDirectionMatrix.clone();
    const upperFLV = frontLineVector.clone();
    upperFLV.y = sides[0].z;
    const upperBLI = backlineIntersect.clone();
    upperBLI.y = sides[0].z;

    // Calculate Rounded Corner
    if (side.childRadius > 0 && side.childAngle > 0) {
      const roundCenterVectorLength = side.childRadius / Math.cos(
        (90 - ((180 - side.childAngle) / 2)) * (Math.PI / 180),
      );
      const roundCenterVector = new THREE.Vector3(0, 0, roundCenterVectorLength);

      roundCenterVector.applyMatrix4(frontLineDirectionMatrix);
      roundCenterVector.applyMatrix4(degreeToMatrix(-side.childAngle));
      const roundCenterPoint = roundCenterVector
        .applyMatrix4(degreeToMatrix(side.childAngle + (180 - side.childAngle) / 2));

      roundCenterPoint.add(frontLineVector);

      const roundCenterRadVector = new THREE.Vector3(0, 0, side.childRadius);
      roundCenterRadVector.applyMatrix4(
        degreeToMatrix(side.childAngle + (180 - side.childAngle) / 2),
      ).multiplyScalar(-1);

      vertices.push(
        backlineIntersect,
        upperBLI,
      );
      faces.push(
        // Back
        new THREE.Face3(faceP1, faceP2, faceCount + 1),
        new THREE.Face3(faceP2, faceCount + 2, faceCount + 1),
        // top
        new THREE.Face3(faceCount + 2, faceP2, faceP4),
      );
      faceP1 = faceCount + 1;
      faceP2 = faceCount + 2;

      const alpha = (90 - (180 - side.childAngle) / 2) * 2;
      for (
        let circleAngle = -(alpha / 2);
        circleAngle <= alpha / 2;
        circleAngle += alpha / 10
      ) {
        const roundOutAngle = degreeToMatrix(circleAngle);
        const roundOutPoint = roundCenterRadVector
          .clone()
          .applyMatrix4(roundEdgeDirectionMatrix)
          .applyMatrix4(roundOutAngle)
          .add(roundCenterPoint);
        const upperROP = roundOutPoint.clone();
        upperROP.y = sides[0].z;
        vertices.push(roundOutPoint);
        vertices.push(upperROP);
        if (circleAngle === -(alpha / 2)) {
          faces.push(
            // Top
            new THREE.Face3(faceP4, faceCount + 4, faceCount + 2),
            // Front
            new THREE.Face3(faceP3, faceCount + 4, faceP4),
            new THREE.Face3(faceP3, faceCount + 3, faceCount + 4),
          );
          faceCount += 4;
          faceP4 = faceCount;
          faceP3 = faceCount - 1;
        } else {
          faces.push(
            // Top
            new THREE.Face3(faceP4, faceCount + 2, faceP2),
            // Front
            new THREE.Face3(faceP3, faceCount + 4, faceP4),
            new THREE.Face3(faceP3, faceCount + 3, faceCount + 4),
          );
          faceCount += 2;
          faceP4 = faceCount;
          faceP3 = faceCount - 1;
        }
      }
    } else {
      vertices.push(
        backlineIntersect,
        upperBLI,
        frontLineVector,
        upperFLV,
      );
      faces.push(
        // Back
        new THREE.Face3(faceP1, faceP2, faceCount + 1),
        new THREE.Face3(faceP2, faceCount + 2, faceCount + 1),
        // Top
        new THREE.Face3(faceP4, faceCount + 4, faceCount + 2),
        new THREE.Face3(faceCount + 2, faceP2, faceP4),
        // Front
        new THREE.Face3(faceP3, faceCount + 4, faceP4),
        new THREE.Face3(faceP3, faceCount + 3, faceCount + 4),
      );
      faceCount += 4;
      faceP1 = faceCount - 3;
      faceP2 = faceCount - 2;
      faceP3 = faceCount - 1;
      faceP4 = faceCount;
    }
  });

  faces.push(
    new THREE.Face3(faceP4, faceP3, faceP1),
    new THREE.Face3(faceP4, faceP1, faceP2),
  );

  return { vertices, faces };
};

const generateMesh = (vertciesAndFaces, texture) => {
  const { vertices, faces } = vertciesAndFaces;
  const geometry = new THREE.Geometry();
  geometry.faces = faces;
  geometry.vertices = vertices;
  geometry.computeFaceNormals();
  geometry.faces.forEach(() => {
    geometry.faceVertexUvs[0].push(
      [new THREE.Vector2(0, 0), new THREE.Vector2(1, 1), new THREE.Vector2(0, 1)],
    );
  });
  const loader = new THREE.TextureLoader();
  const material = new THREE.MeshLambertMaterial({
    map: loader.load(ASSET_FOLDER + texture),
    side: THREE.DoubleSide,
  });
  // new THREE.MeshBasicMaterial({ map: loader.load(ASSET_FOLDER + texture) });
  return new THREE.Mesh(geometry, material);
  // return new THREE.Line(geometry, material);
};

/**
 * Generates Counter Labels
 *
 * @param sides List of side dimensions
 * @return THREE.Object3D with representation of numbers.
 */
const generateCounterLabel = (sides) => {
  const frontLineRotationMatrix = degreeToMatrix(0);
  const frontLineVector = new THREE.Vector3(0, 0, 0);
  const numberGroup = new THREE.Object3D();

  sides.forEach((side, index) => {
    const frontLineDirectionVector = new THREE.Vector3(0, 0, side.y);
    frontLineDirectionVector.applyMatrix4(frontLineRotationMatrix);
    const textDirectionVector = new THREE.Vector3(0, 0, (side.y / 2) - 0.5);
    textDirectionVector.applyMatrix4(frontLineRotationMatrix);

    const textGeo = new THREE.TextGeometry(String(index + 1), {
      font: new THREE.Font(optimerFont),
      size: 1,
      height: 0.1,
      curveSegments: 30,
    });
    textGeo.computeBoundingBox();
    textGeo.computeVertexNormals();

    const loader = new THREE.TextureLoader();
    const material = new THREE.MeshLambertMaterial({
      map: loader.load(`${ASSET_FOLDER}black.png`),
      side: THREE.DoubleSide,
    });
    const textObject = new THREE.Mesh(textGeo, material);
    // rotate 90 degree
    textObject.matrix.multiply(frontLineRotationMatrix.clone().multiply(degreeToMatrix(270)));
    textObject.rotation.setFromRotationMatrix(textObject.matrix);
    textObject.position.set(
      frontLineVector.x + textDirectionVector.x,
      0.5,
      frontLineVector.z + textDirectionVector.z,
    );
    numberGroup.add(textObject);

    frontLineRotationMatrix.multiply(degreeToMatrix(side.childAngle));
    frontLineVector.add(frontLineDirectionVector);
  });

  return numberGroup;
};

const calculateCentroid = (mesh) => {
  mesh.geometry.computeBoundingBox();
  const { boundingBox } = mesh.geometry;

  const x0 = boundingBox.min.x;
  const x1 = boundingBox.max.x;
  const y0 = boundingBox.min.y;
  const y1 = boundingBox.max.y;
  const z0 = boundingBox.min.z;
  const z1 = boundingBox.max.z;

  const bWidth = (x0 > x1) ? x0 - x1 : x1 - x0;
  const bHeight = (y0 > y1) ? y0 - y1 : y1 - y0;
  const bDepth = (z0 > z1) ? z0 - z1 : z1 - z0;

  const centroidX = x0 + (bWidth / 2) + mesh.position.x;
  const centroidY = y0 + (bHeight / 2) + mesh.position.y;
  const centroidZ = z0 + (bDepth / 2) + mesh.position.z;

  return { x: centroidX, y: centroidY, z: centroidZ };
};

export default {
  generate: (dimensions) => {
    const group = new THREE.Object3D();
    let centroid;

    if (
      (!dimensions[0].raisedHeight
        || dimensions[0].raisedHeight === 0)
      && (!dimensions[0].raisedWidth
        || dimensions[0].raisedWidth === 0)
    ) {
      // Main Table
      const sides = dimensions;
      const bottomSides = sides.map((side, index) => {
        if (index === sides.length - 1) {
          return {
            ...side,
            z: 0.2,
            y: side.y - 0.2,
            x: side.x - 0.4,
          };
        }
        if (index === 0) {
          return {
            ...side,
            z: 0.2,
            y: side.y - 0.2,
            x: side.x - 0.4,
          };
        }
        return {
          ...side,
          z: 0.2,
          x: side.x - 0.4,
        };
      });
      const bottomMesh = generateMesh(calculateVerticesAndFaces(bottomSides), 'grey.png');
      bottomMesh.position.set(0, 0, 0.2);
      group.add(bottomMesh);

      // Main Table
      const middleMeshSides = sides.map((side, index) => {
        if (index === sides.length - 1 && index === 0) {
          return {
            ...side,
            z: side.z - 0.4,
            x: side.x,
            y: side.y - 0.4,
          };
        }
        if (index === sides.length - 1) {
          return {
            ...side,
            z: side.z - 0.4,
            x: side.x,
            y: side.y - 0.2,
          };
        }
        if (index === 0) {
          return {
            ...side,
            z: side.z - 0.4,
            x: side.x,
            y: side.y - 0.2,
          };
        }
        return {
          ...side,
          z: side.z - 0.4,
          x: side.x,
          y: side.y,
        };
      });
      const middleMesh = generateMesh(calculateVerticesAndFaces(middleMeshSides), 'white.png');
      middleMesh.position.set(0, 0.2, 0.2);
      group.add(middleMesh);

      const topMesh = generateMesh(calculateVerticesAndFaces(sides.map((side) => ({
        ...side,
        z: 0.2,
      }))), 'black.png');
      topMesh.position.set(0, sides[0].z - 0.2, 0);
      group.add(topMesh);

      // SIDE
      const sideCubeGeometry = new THREE.BoxGeometry(sides[0].x, sides[0].z - 0.2, 0.2);
      const loader = new THREE.TextureLoader();
      const sideMaterial = new THREE.MeshLambertMaterial({
        map: loader.load(`${ASSET_FOLDER}black.png`),
        side: THREE.DoubleSide,
      });
      const sideCube = new THREE.Mesh(sideCubeGeometry, sideMaterial);
      sideCube.position.set(sides[0].x / 2, (sides[0].z - 0.2) / 2, 0.1);
      group.add(sideCube);

      // BackSIDE
      const totalRotation = degreeToMatrix(0);
      sides.forEach((side) => {
        totalRotation.multiply(degreeToMatrix(side.childAngle));
      });
      const backSideCube = new THREE.Mesh(sideCubeGeometry, sideMaterial);
      backSideCube.rotation.setFromRotationMatrix(
        backSideCube.matrix.clone().multiply(totalRotation),
      );
      const halfTranslationVector = new THREE.Vector3(sides[0].x / 2, 0, -0.1);
      halfTranslationVector.applyMatrix4(totalRotation);
      backSideCube.position.set(
        topMesh.geometry.vertices[[topMesh.geometry.vertices.length - 1]].x,
        (sides[0].z - 0.2) / 2,
        topMesh.geometry.vertices[[topMesh.geometry.vertices.length - 1]].z,
      ).add(halfTranslationVector);
      group.add(backSideCube);

      centroid = calculateCentroid(bottomMesh);
    }

    // Raised Section
    if (
      dimensions[0].raisedHeight
      && dimensions[0].raisedHeight !== 0
      && dimensions[0].raisedWidth
      && dimensions[0].raisedWidth !== 0
    ) {
      // BOTTOM
      const sides = dimensions;
      const bottomSides = sides.map((side, index) => {
        if (index === sides.length - 1) {
          return {
            ...side,
            z: 0.2,
            y: side.y - 0.2,
            x: side.raisedWidth - 0.4,
          };
        }
        if (index === 0) {
          return {
            ...side,
            z: 0.2,
            y: side.y,
            x: side.raisedWidth - 0.4,
          };
        }
        return {
          ...side,
          z: 0.2,
          x: side.raisedWidth - 0.4,
        };
      });
      const bottomMesh = generateMesh(calculateVerticesAndFaces(bottomSides), 'grey.png');
      bottomMesh.position.set(0, 0, 0);
      group.add(bottomMesh);

      // Main Table
      const middleMeshSides = sides.map((side, index) => {
        if (index === sides.length - 1 && index === 0) {
          return {
            ...side,
            z: side.z - 0.2,
            x: side.x,
            y: side.y - 0.4,
          };
        }
        if (index === sides.length - 1) {
          return {
            ...side,
            z: side.z - 0.2,
            x: side.x,
            y: side.y - 0.2,
          };
        }
        if (index === 0) {
          return {
            ...side,
            z: side.z - 0.2,
            x: side.x,
            y: side.y - 0.2,
          };
        }
        return {
          ...side,
          z: side.z - 0.2,
          x: side.x,
          y: side.y,
        };
      });
      const middleMesh = generateMesh(calculateVerticesAndFaces(middleMeshSides), 'white.png');
      middleMesh.position.set(0, 0.2, 0.2);
      middleMesh.receiveShadow = true;
      group.add(middleMesh);

      // TOP
      const topMesh = generateMesh(calculateVerticesAndFaces(sides.map((side) => ({
        ...side,
        z: 0.2,
        x: side.raisedWidth,
      }))), 'black.png');
      topMesh.position.set(0, sides[0].z + sides[0].raisedHeight - 0.2, 0);
      topMesh.receiveShadow = true;
      group.add(topMesh);

      // SIDE
      const sideCubeGeometry = new THREE.BoxGeometry(
        sides[0].raisedWidth,
        sides[0].z + sides[0].raisedHeight - 0.2,
        0.2,
      );
      const loader = new THREE.TextureLoader();
      const sideMaterial = new THREE.MeshLambertMaterial({
        map: loader.load(`${ASSET_FOLDER}black.png`),
        side: THREE.DoubleSide,
      });
      const sideCube = new THREE.Mesh(sideCubeGeometry, sideMaterial);
      sideCube.position.set(
        sides[0].raisedWidth / 2,
        (sides[0].z + sides[0].raisedHeight - 0.2) / 2,
        0.1,
      );

      sideCube.receiveShadow = true;
      group.add(sideCube);

      // BackSIDE
      const totalRotation = degreeToMatrix(0);
      sides.forEach((side) => {
        totalRotation.multiply(degreeToMatrix(side.childAngle));
      });
      const backSideCube = new THREE.Mesh(sideCubeGeometry, sideMaterial);
      backSideCube.rotation.setFromRotationMatrix(
        backSideCube.matrix.clone().multiply(totalRotation),
      );
      const halfTranslationVector = new THREE.Vector3(sides[0].raisedWidth / 2, 0, -0.1);
      halfTranslationVector.applyMatrix4(totalRotation);
      backSideCube.position.set(
        topMesh.geometry.vertices[[topMesh.geometry.vertices.length - 1]].x,
        (sides[0].z + sides[0].raisedHeight - 0.2) / 2,
        topMesh.geometry.vertices[[topMesh.geometry.vertices.length - 1]].z,
      ).add(halfTranslationVector);
      backSideCube.receiveShadow = true;
      group.add(backSideCube);

      // // Back
      // const backMeshSides = sides.map((side, index) => {
      //   if (index === sides.length - 1) {
      //     return {
      //       ...side,
      //       z: 0.2,
      //       y: side.y,
      //       x: side.x,
      //     };
      //   }
      //   if (index === 0) {
      //     return {
      //       ...side,
      //       z: 0.2,
      //       y: side.y,
      //       x: side.x,
      //     };
      //   }
      //   return {
      //     ...side,
      //     z: 0.2,
      //     y: side.y,
      //     x: side.x,
      //   };
      // });
      // const backMesh = generateMesh(calculateVerticesAndFaces(backMeshSides), 'grey.png');
      // backMesh.position.set(0, sides[0].z - 0.2, 0);
      // group.add(backMesh);

      centroid = calculateCentroid(middleMesh);
    }
    group.add(generateCounterLabel(dimensions));

    return { group, centroid };
  },
};
